import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { autorunAsync, observable } from 'mobx';

import GlobalEvent from '../../utils/globalEvent';
import findAncestor from '../../utils/findAncestor';
import globalTimer from '../../utils/globalTimer';
import Portal from '../portal';
import './style.scss';

const SIDE_OFFSET = 10;
export const POSITIONS = [
	'top',
	'bottom',
	'left',
	'right',
	'top-left-align',
	'top-right-align',
	'bottom-left-align',
	'bottom-right-align',
];

@observer
export default class Popover extends React.Component {
	
	static propTypes = {
		id: PropTypes.string,
		content: PropTypes.any.isRequired,
		position: PropTypes.oneOf(POSITIONS),
		trigger: PropTypes.oneOf(['hover', 'click', 'always']),
		offset: PropTypes.number,
		delay: PropTypes.number,
		startOffset: PropTypes.number,
		className: PropTypes.string,
		onClick: PropTypes.func,
		portalClassName: PropTypes.string,
		style: PropTypes.object,
		instance: PropTypes.func,
		onActivate: PropTypes.func,
		zIndex: PropTypes.number,
	};
	
	static defaultProps = {
		id: 'popover',
		position: 'top',
		trigger: 'hover',
		offset: 10,
		startOffset: 0,
		delay: 150,
		className: '',
		portalClassName: '',
		style: {},
		zIndex: 9000,
	};

	@observable active = false;
	@observable oldPortal = false;

	constructor(props) {
		super(props);
		if (props.trigger === 'always') this.active = true;
	}

	componentDidMount() {
		GlobalEvent.subscribe('mousedown', this.onMouseDown);
		GlobalEvent.subscribe('mousemove', this.onMouseMove);
		if (document.getElementById('main-content')) document.getElementById('main-content').addEventListener('scroll', this.onScroll);

		if (this.props.instance) {
			this.props.instance({
				close: this.close,
				isActive: () => this.active,
			});
		}
	}
	
	componentWillUnmount() {
		GlobalEvent.unsubscribe('mousedown', this.mouseDown);
		GlobalEvent.unsubscribe('mousemove', this.mouseMove);
		if (document.getElementById('main-content')) document.getElementById('main-content').removeEventListener('scroll', this.onScroll);
	}

	onMouseDown = e => {
		if (this.active && this.contentNode && this.node) {
			// console.log('down', e.target);
			if (!this.contentNode.contains(e.target) && !this.node.contains(e.target)) {
				this.close();
			}
			else {
				const closestPopover = findAncestor(e.target, 'ui-popover-wrapper');
				if (closestPopover && closestPopover !== e.target) {
					this.close();
				}
			}
		}
	};

	onScroll = () => {
		// console.log('onScroll');
		this.active && this.close();
	};

	onMouseMove = e => {
		if (this.props.trigger === 'hover') {
			if (this.node && !this.node.contains(e.target)) {
				if (globalTimer.element === this.node) {
					this.clearTimer('mouseMove outside');
				}
				if (this.active && this.contentNode && !this.contentNode.contains(e.target)) {
					this.close();
				}
			}
		}
	};
	
	onWrapperMount = el => {
		this.node = el;
	};
	
	clearTimer = (reason) => {
		// console.log('clearTimer', reason, globalTimer.element);
		if (this.node) {
			this.node.style.transition = 'none';
			this.node.classList.remove('activated');
		}
		clearTimeout(globalTimer.timer);
		globalTimer.element = null;
		globalTimer.timer = null;
	};
	
	onPortalMount = el => {
		this.contentNode = el;
		if (el) {
			document.body.addEventListener('scroll', this.onScroll);
			window.addEventListener('scroll', this.onScroll);
			el.className = 'ui-popover ui-popover-' + this.props.position + ' ' + this.props.portalClassName;
			setTimeout(() => {
				el.classList.add('active');
				this.placePopover();
			}, 0);
		}
		else {
			document.body.removeEventListener('scroll', this.onScroll);
			window.removeEventListener('scroll', this.onScroll);
		}
	};
	
	placePopover() {
		if (this.node && this.contentNode) {
			const rect = this.node.getBoundingClientRect();
			let left = 0;
			let top = 0;
			let right = 'auto';
			let bottom = 'auto';
			
			let position = this.props.position;
			const leftMost = rect.left - this.contentNode.offsetWidth + 10;
			const rightMost = rect.left + this.contentNode.offsetWidth - 10;
			const topMost = rect.top - this.contentNode.offsetHeight + 10;
			const bottomMost = rect.bottom + this.contentNode.offsetHeight - 10;
			
			if (rightMost > window.innerWidth) {
				if (position === 'top' || position === 'top-left-align') {
					position = 'top-right-align';
				}
				else if (position === 'bottom' || position === 'bottom-left-align') {
					position = 'bottom-right-align';
				}
				else if (position === 'right') {
					position = 'left';
				}
				console.log('overflow at right!', rightMost, window.innerWidth, this.props.position, '->', position);
			}
			
			if (leftMost < 0) {
				if (position === 'top' || position === 'top-right-align') {
					position = 'top-left-align';
				}
				else if (position === 'bottom' || position === 'bottom-right-align') {
					position = 'bottom-left-align';
				}
				else if (position === 'left') {
					position = 'right';
				}
				console.log('overflow at left!', rightMost, window.innerWidth, this.props.position, '->', position);
			}
			
			if (topMost < 0) {
				position = position.replace(/top/, 'bottom');
				console.log('overflow at top!', bottomMost, window.innerHeight, this.props.position, '->', position);
			}
			else if (bottomMost > window.innerHeight) {
				position = position.replace(/bottom/, 'top');
				console.log('overflow at bottom!', bottomMost, window.innerHeight, this.props.position, '->', position);
			}
			
			switch (position) {
				case 'right': {
					left = rect.left + rect.width + this.props.offset;
					top = rect.top + (rect.height - this.contentNode.offsetHeight) / 2;
					break;
				}
				case 'left': {
					left = rect.left - this.contentNode.offsetWidth - this.props.offset;
					top = rect.top + (rect.height - this.contentNode.offsetHeight) / 2;
					break;
				}
				case 'bottom': {
					left = rect.left + (rect.width - this.contentNode.offsetWidth) / 2;
					top = rect.top + rect.height + this.props.offset;
					break;
				}
				case 'topstart':
				case 'top-left-align': {
					left = rect.left + this.props.startOffset - SIDE_OFFSET;
					top = rect.top - this.contentNode.offsetHeight - this.props.offset;
					break;
				}
				case 'top-right-align': {
					left = rect.right - this.contentNode.offsetWidth + SIDE_OFFSET;
					top = rect.top - this.contentNode.offsetHeight - this.props.offset;
					break;
				}
				case 'bottom-left-align': {
					left = rect.left + this.props.startOffset - SIDE_OFFSET;
					top = rect.top + rect.height + this.props.offset;
					break;
				}
				case 'bottom-right-align': {
					left = rect.right - this.contentNode.offsetWidth + SIDE_OFFSET;
					top = rect.top + rect.height + this.props.offset;
					break;
				}
				default:
				case 'top': {
					left = rect.left - this.contentNode.offsetWidth / 2 + rect.width / 2;
					top = rect.top - this.contentNode.offsetHeight - this.props.offset;
					break;
				}
			}

			this.setStyleProperties({ left, top, right, bottom });
			if (this.props.position !== position) {
				this.contentNode.classList.add('ui-popover-' + position);
				this.contentNode.classList.remove('ui-popover-' + this.props.position);
			}
		}
	}

	setStyleProperties = obj => {
		Object.keys(obj).forEach(key => {
			const rawValue = obj[key];
			const value = typeof rawValue === 'number' ? rawValue + 'px' : rawValue;
			this.contentNode.style[key] = value;
		});
	};

	show = () => {
		this.clearTimer('show');
		if (this.node) {
			this.node.style.transition = 'all 3000ms';
			this.node.classList.add('activated');
			globalTimer.element = this.node;
		}
		globalTimer.timer = setTimeout(() => {
			this.active = true;
			if (this.node) {
				this.node.style.transition = 'none';
				this.node.classList.remove('activated');
			}
		}, this.props.delay);
	};

	close = () => {
		this.clearTimer('close');
		if (this.contentNode) this.contentNode.classList.remove('active');
		this.active = false;
		if (this.props.onClose) this.props.onClose();
	};
	
	onMouseEnter = () => {
		setTimeout(() => {
			if (this.props.trigger === 'hover') {
				this.show();
				if (this.props.onActivate) this.props.onActivate();
			}
		}, 16);
	};

	onClick = e => {
		if (this.props.trigger === 'click') {
			e.stopPropagation();
			this.active = !this.active;
			if (this.active) {
				if (this.props.onActivate) this.props.onActivate();
			}
			else {
				if (this.props.onClose) this.props.onClose();
			}
		}
		if (this.props.onClick) this.props.onClick();
	};

	render() {
		return <div className={'ui-popover-wrapper ' + this.props.className}
		            style={this.props.style}
		            ref={this.onWrapperMount}
		            onMouseEnter={this.onMouseEnter}
		            // onMouseLeave={this.onMouseLeave}
		            onClick={this.onClick}>
			{this.props.children}
			{this.active &&
			<Portal portalId={this.props.id}
			        className={'ui-popover ui-popover-' + this.props.position + ' ' + this.props.portalClassName}
			        zIndex={this.props.zIndex}
			        getRef={this.onPortalMount}>
				{this.props.content}
				<span className="arrow-underlay" />
				<span className="arrow"/>
			</Portal>}
		</div>
	}

}
